import { Component, inject, Injector, OnDestroy, OnInit, Signal } from '@angular/core';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { isKnownTab, Tab } from '@modules/common/types/tab';
import { ModalService } from '@modules/modal/services/modal.service';
import { NotebookListStateService } from '@modules/notes/services/notebook-list-state.service';
import { NotebooksService } from '@modules/notes/services/notebooks.service';
import { NotesListStateService } from '@modules/notes/services/notes-list-state.service';
import { NotesService } from '@modules/notes/services/notes.service';
import { NotesAppStateService } from '@modules/notes/services/state.service';
import { ListState } from '@modules/notes/types/list-state';
import { Note } from '@modules/notes/types/note';
import { Notebook } from '@modules/notes/types/notebook';
import { NotebooksFilters } from '@modules/notes/types/notebooks-filters';
import { NotesFilters } from '@modules/notes/types/notes-filters';
import { VirtualFolder } from '@modules/notes/types/virtual-folder';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateKey } from '@modules/settings/types/state-key';
import { SplitViewService } from '@modules/split-view/services/split-view.service';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { Application } from '@modules/common/types/application';

@Component({
  selector: 'app-notes-list-container',
  templateUrl: './notes-list-container.component.html',
  styleUrls: ['./notes-list-container.component.less'],
  standalone: false,
  providers: [
    ...NotesListStateService.providers({
      stateKey: StateKey.notesListState,
      withAdvancedSearch: true,
    }),
    ...NotebookListStateService.providers({
      stateKey: StateKey.notebooksListState,
      withAdvancedSearch: true,
    }),
  ],
})
export class NotesListContainerComponent implements OnInit, OnDestroy {
  public minimized: boolean;
  public searchQuery: string;
  public listState: ListState;
  public sidebarFilter: VirtualFolder;
  public quickNotebook: Notebook;
  public quickNote: Note;
  public loading: boolean;
  public tabs: Tab<Exclude<ListState, 'tabs'>>[] = [
    { title: 'Notebooks', value: 'notebooks' },
    { title: 'Notes', value: 'notes' },
  ];
  public selectedTab: Exclude<ListState, 'tabs'> = 'notes';
  public notebooksFilters: NotebooksFilters;
  public notesFilters: NotesFilters;
  public debug: 'score' = null;
  public containerName: string | null = null;

  /**
   * The tenant the rail's selected section names — `null` for the signed-in user's own notes, a space
   * or member-tenant id otherwise. Fed to the compose dialog and the sidebar quick form so a create
   * lands in the workspace the user is looking at (#1016 r2).
   *
   * It is deliberately NOT applied to `NotesFilters`/`NotebooksFilters` and never becomes a query
   * param: the tenant reaches the wire as `space_id`, stamped by `createSpacesInterceptor` from
   * `NotesTenantScopeService`. `getTeam()` sits in the two `combineLatest` above for its IDENTITY
   * effect — a section switch rebuilds the filters object, and `BaseStitchListComponent.ngOnChanges`
   * refetches on a `filters` REFERENCE change. Without it the switch is invisible to the list: that
   * class has no `getRefreshRequired` subscriber, so `NotesService.forceRefresh()` cannot re-issue this
   * request, and #1016 it1 shipped a rail whose sections both showed whatever the last write left on
   * screen. Do not "simplify" the unused destructure away.
   */
  protected selectedTenantId: string | null = null;

  private readonly notesListStateService = inject(NotesListStateService, { self: true });
  private readonly notebookListStateService = inject(NotebookListStateService, { self: true });
  private readonly advancedSearchService = inject(AdvancedSearchService);
  private readonly notesService = inject(NotesService);
  private readonly notebooksService = inject(NotebooksService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;

  private alive = new Subject<void>();

  protected readonly isAdvancedSearchApplied: Signal<boolean> = toSignal(
    this.advancedSearchService.isAdvancedSearchApplied(Application.notes),
    { initialValue: false },
  );

  constructor(
    private splitViewService: SplitViewService,
    private stateService: NotesAppStateService,
    private modalService: ModalService,
    private searchService: AdvancedSearchService,
    private injector: Injector,
  ) {
    combineLatest([
      this.notebookListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.stateService.getTeam(),
      this.stateService.getVirtualFolderReselections(),
    ])
      .pipe(takeUntil(this.alive))
      // The 5th element is intentionally not destructured: `getTeam()` is in the tuple for its
      // identity effect only, and `selectedTenantId` has a single writer in the notes stream below.
      .subscribe(([list, search, folder, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState/applyVirtualFolder read it to reckon their day
        // windows in the account zone (SBUX-829).
        this.notebooksFilters = new NotebooksFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });

    combineLatest([
      this.notesListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.stateService.getTeam(),
      this.stateService.getVirtualFolderReselections(),
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone, tenantId]) => {
        this.selectedTenantId = tenantId;
        if (folder === 'assigned_to_me') {
          this.notesFilters = new NotesFilters({ displayTimeZone })
            .applyAdvancedFilters(search)
            .applyListState(list)
            .applyVirtualFolder(folder);
          return;
        }
        this.notesFilters = new NotesFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });
  }

  public ngOnInit() {
    this.splitViewService
      .getMinimized('notesList')
      .pipe(takeUntil(this.alive))
      .subscribe((minimized: boolean) => {
        this.minimized = minimized;
      });

    this.stateService
      .getTabs()
      .pipe(takeUntil(this.alive))
      .subscribe((state: ListState) => {
        this.listState = state;
        if (this.listState !== 'tabs') {
          this.selectedTab = this.listState;
        }
        // Unconditional, not just on the non-'tabs' branch above: a container recreated by
        // stchSpaceRerender (e.g. on a space switch) re-inits `selectedTab` to its field default, but
        // the app-state service instance survives the switch and keeps whatever tab was last published
        // — republishing here on every emission, including while folder-mapped to 'tabs', overwrites
        // that stale value as soon as this container exists instead of only once the folder next
        // changes to a concrete one.
        this.stateService.setSelectedTab(this.selectedTab);
      });

    combineLatest([this.searchService.getState(), this.stateService.getVirtualFolder()])
      .pipe(takeUntil(this.alive))
      .subscribe(([state, folder]) => {
        this.sidebarFilter = folder;
        this.containerName = state[Application.notes]?.containerName ?? null;
        this.quickNote = Note.fromAdvancedState(state, folder);
        this.quickNotebook = Notebook.fromAdvancedState(state, folder);
      });
  }

  ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  protected handleSelectedTabChange(tab: string): void {
    if (!isKnownTab<Exclude<ListState, 'tabs'>>(tab, this.tabs)) {
      return;
    }
    this.selectedTab = tab;
    this.stateService.setSelectedTab(tab);
  }

  handleNewNote(note = new Note()) {
    this.stateService.setMainView(note);
  }

  handleNewNotebook(notebook = new Notebook()) {
    this.stateService.setMainView(notebook);
  }

  handleDoubleClickNote(note: Note) {
    this.modalService.openFullForm(note, this.injector);
  }

  handleClickNote(note: Note) {
    this.stateService.setMainView(note);
  }

  handleDoubleClickNotebook(notebook: Notebook) {
    this.modalService.openFullForm(notebook, this.injector);
  }

  handleClickNotebook(notebook: Notebook) {
    this.stateService.setMainView(notebook);
  }

  doDebug() {
    switch (this.debug) {
      case 'score':
        this.debug = null;
        break;
      default:
        this.debug = 'score';
    }
  }
}
